import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Button, Row, Col, Form, Card } from 'react-bootstrap'
import ModalContainer from './ModalContainer'
import Message from '../../shared_components/Message'

export const JitaiSetting = ({ 
  group, 
  index, 
  qx_list, 
  jitai_variables, 
  jitai_messages, 
  jitai_decision_rules,
  jitai_resps,
  handleDelete 
}) => {
  //It is needed to check if the logged in user is an admin
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo, logout } = userLogin

  const [show, setShow] = useState(false)

  const [taskName, setTaskName] = useState('')
  const [collectingResponses, setCollectingResponses] = useState([])
  const [preMessageQxResp, setPreMessageQxResp] = useState(null)
  const [preMessageCustomResps, setPreMessageCustomResps] = useState([])
  const [promptTimeType, setPromptTimeType] = useState('')
  const [promptTime, setPromptTime] = useState('09:00')
  const [promptVariable, setPromptVariable] = useState(null)
  const [timezone, setTimezone] = useState('')
  const [expiryDurationMinutes, setExpiryDurationMinutes] = useState(30)
  const [presentMessageDecisionRule, setPresentMessageDecisionRule] = useState(null)
  const [messageSettings, setMessageSettings] = useState([])

  const [saveError, setSaveError] = useState('')

  useEffect(() => {
    const jitaiSetting = group?.jitai_setting_list?.[index]
    if (!jitaiSetting) return
    setTaskName(jitaiSetting.task_name || '')
    setCollectingResponses(jitaiSetting.collecting_responses || [])
    const pre = jitaiSetting.pre_message_observations || {}
    setPreMessageQxResp(pre.qx_resp || null)
    setPreMessageCustomResps(
      Array.isArray(pre.custom_resps)
        ? pre.custom_resps.map(c => ({ jitai_resp: c.jitai_resp || c }))
        : []
    )
    setPromptTimeType(jitaiSetting.prompt_time_type)
    setPromptTime(jitaiSetting.prompt_time)
    setPromptVariable(jitaiSetting.prompt_variable)
    setTimezone(jitaiSetting.timezone)
    setExpiryDurationMinutes(jitaiSetting.expiry_duration_minutes || 30)
    setPresentMessageDecisionRule(jitaiSetting.present_message_decision_rule || null)
    setMessageSettings(jitaiSetting.message_settings || [])
  }, [group, index])

  const handleOpen = () => {
    setShow(true)
  }

  const handleMessageSettingAdd = () => {
    let updatedMessageSettings = [...messageSettings]
    updatedMessageSettings.push({
      probability: '',
      message_type: '',
      jitai_message: null,
      jitai_decision_rule: null,
    })
    setMessageSettings(updatedMessageSettings)
  }

  const handleMessageSettingDelete = (i) => {
    let updatedMessageSettings = [...messageSettings]
    updatedMessageSettings.splice(i, 1)
    setMessageSettings(updatedMessageSettings)
  }

  const handleCollectingResponseAdd = () => {
    let updated = [...collectingResponses]
    updated.push({
      jitai_resp: '',
      collected_at_minutes_after_task: 0,
      qx_respond_in: '',
      qx_reminder_interval: '',
    })
    setCollectingResponses(updated)
  }

  const handleCollectingResponseDelete = (i) => {
    let updated = [...collectingResponses]
    updated.splice(i, 1)
    setCollectingResponses(updated)
  }

  const handlePreMessageCustomAdd = () => {
    setPreMessageCustomResps([...preMessageCustomResps, { jitai_resp: '' }])
  }

  const handlePreMessageCustomDelete = (i) => {
    const updated = [...preMessageCustomResps]
    updated.splice(i, 1)
    setPreMessageCustomResps(updated)
  }

  const getJitaiRespById = (respId) => {
    if (!jitai_resps || !respId) return null
    return jitai_resps.find(r => r._id === respId || r._id === respId?._id)
  }

  const checkDependencies = () => {
    if (!jitai_variables || jitai_variables.length === 0) {
      return 'At least one JITAI Variable is required before configuring JITAI Settings'
    }
    if (!jitai_messages || jitai_messages.length === 0) {
      return 'At least one JITAI Message is required before configuring JITAI Settings'
    }
    return ''
  }

  ////////// Validator ///////////

  const checkTaskName = () => {
    if (!taskName || taskName.trim().length === 0) {
      return 'Task name is required'
    }
    const trimmed = taskName.trim()
    const otherSettings = group.jitai_setting_list.filter((_, i) => i !== index)
    const duplicate = otherSettings.find(
      (s) => s.task_name && s.task_name.trim() === trimmed
    )
    if (duplicate) {
      return 'Task name must be unique within the same participant group'
    }
    return ''
  }

  const checkPromptTimeType = () => {
    if (!promptTimeType) {
      return 'Prompt time type must be selected (Fixed or Variable)'
    }
    return ''
  }

  const checkPromptTime = () => {
    if (promptTimeType === 'Fixed' && !promptTime) {
      return 'Prompt time must be provided when Fixed is selected'
    }
    if (promptTimeType === 'Fixed' && promptTime) {
      // Prevent setting time between 00:00 and 01:00 (exclusive)
      const [hours, minutes] = promptTime.split(':').map(Number)
      if (hours === 0 && minutes >= 0 && minutes < 60) {
        return 'Prompt time cannot be set between 00:00 and 00:59 as tasks may not be generated by the daily cron job'
      }
    }
    if (promptTimeType === 'Variable' && !promptVariable) {
      return 'JITAI Variable must be selected when Variable is selected'
    }
    return ''
  }

  const checkExpiryDuration = () => {
    if (!expiryDurationMinutes || expiryDurationMinutes === '') {
      return 'Expiry duration must be provided'
    }
    const duration = Number(expiryDurationMinutes)
    if (isNaN(duration) || duration < 1 || duration > 1440) {
      return 'Expiry duration must be between 1 and 1440 minutes (1 min to 24 hours)'
    }
    return ''
  }

  const checkMessageSettings = () => {
    if (messageSettings.length === 0) {
      return 'At least one message setting must be added'
    }

    for (let i = 0; i < messageSettings.length; i++) {
      const msg = messageSettings[i]
      
      if (!msg.probability || msg.probability === '') {
        return `Message setting ${i + 1}: Probability is required`
      }
      
      if (msg.probability < 1 || msg.probability > 100) {
        return `Message setting ${i + 1}: Probability must be between 1 and 100`
      }
      
      if (!msg.message_type) {
        return `Message setting ${i + 1}: Message type must be selected`
      }
      
      if (msg.message_type === 'Generic' && !msg.jitai_message) {
        return `Message setting ${i + 1}: JITAI Message must be selected for Generic type`
      }
      
      if (msg.message_type === 'Personalized' && !msg.jitai_decision_rule) {
        return `Message setting ${i + 1}: JITAI Decision Rule must be selected for Personalized type`
      }
      
      // NoMessage type does not require jitai_message or jitai_decision_rule
    }

    // Warning if probabilities don't sum to 100
    const totalProbability = messageSettings.reduce((sum, msg) => sum + Number(msg.probability || 0), 0)
    if (totalProbability !== 100) {
      return `Total probability is ${totalProbability}% (should be 100%)`
    }

    return ''
  }

  const checkPreMessageObservations = () => {
    if (preMessageQxResp) {
      const r = getJitaiRespById(preMessageQxResp)
      if (!r) {
        return 'Pre-Message Observations: selected qx response not found'
      }
      if (r.resp_type !== 'qx') {
        return 'Pre-Message Observations: selected response must have resp_type="qx"'
      }
    }
    const seenCustomIds = new Set()
    for (let i = 0; i < preMessageCustomResps.length; i++) {
      const entry = preMessageCustomResps[i]
      if (!entry.jitai_resp) {
        return `Pre-Message Custom Response ${i + 1}: must select a JITAI Response`
      }
      const r = getJitaiRespById(entry.jitai_resp)
      if (!r) {
        return `Pre-Message Custom Response ${i + 1}: selected response not found`
      }
      if (r.resp_type !== 'custom') {
        return `Pre-Message Custom Response ${i + 1}: must have resp_type="custom"`
      }
      const idStr = String(entry.jitai_resp)
      if (seenCustomIds.has(idStr)) {
        return `Pre-Message Custom Response ${i + 1}: duplicate of an earlier entry (${r.code})`
      }
      seenCustomIds.add(idStr)
    }
    return ''
  }

  const checkCollectingResponses = () => {
    for (let i = 0; i < collectingResponses.length; i++) {
      const resp = collectingResponses[i]
      
      if (!resp.jitai_resp) {
        return `Response ${i + 1}: JITAI Response must be selected`
      }
      
      if (resp.collected_at_minutes_after_task === '' || resp.collected_at_minutes_after_task === undefined) {
        return `Response ${i + 1}: Minutes after task is required`
      }
      
      const minutes = Number(resp.collected_at_minutes_after_task)
      if (isNaN(minutes) || minutes < 0) {
        return `Response ${i + 1}: Minutes after task must be a non-negative number`
      }

      // Check if the selected jitai_resp is of 'qx' type
      const selectedResp = getJitaiRespById(resp.jitai_resp)
      if (selectedResp && selectedResp.resp_type === 'qx') {
        if (resp.qx_respond_in !== '' && resp.qx_respond_in !== undefined) {
          const respondIn = Number(resp.qx_respond_in)
          if (isNaN(respondIn) || respondIn < 1) {
            return `Response ${i + 1}: Response window must be at least 1 minute`
          }
        }
        if (resp.qx_reminder_interval !== '' && resp.qx_reminder_interval !== undefined) {
          const interval = Number(resp.qx_reminder_interval)
          if (isNaN(interval) || interval < 1) {
            return `Response ${i + 1}: Reminder interval must be at least 1 minute`
          }
        }
      }
    }

    return ''
  }

  /////////////////////////////////

  const handleClose = () => {
    setSaveError('')
    let error = ''

    if (error.length === 0) error = checkTaskName()
    if (error.length === 0) error = checkDependencies()
    if (error.length === 0) error = checkPromptTimeType()
    if (error.length === 0) error = checkPromptTime()
    if (error.length === 0) error = checkExpiryDuration()
    if (error.length === 0) error = checkMessageSettings()
    if (error.length === 0) error = checkCollectingResponses()
    if (error.length === 0) error = checkPreMessageObservations()

    if (error.length === 0) {
      // Sanitize collecting responses - convert string values to numbers where needed
      const sanitizedCollectingResponses = collectingResponses.map(resp => ({
        jitai_resp: resp.jitai_resp,
        collected_at_minutes_after_task: Number(resp.collected_at_minutes_after_task),
        qx_respond_in: resp.qx_respond_in !== '' && resp.qx_respond_in !== undefined 
          ? Number(resp.qx_respond_in) 
          : undefined,
        qx_reminder_interval: resp.qx_reminder_interval !== '' && resp.qx_reminder_interval !== undefined 
          ? Number(resp.qx_reminder_interval) 
          : undefined,
      }))

      group.jitai_setting_list[index].task_name = taskName.trim()
      group.jitai_setting_list[index].collecting_responses = sanitizedCollectingResponses
      group.jitai_setting_list[index].pre_message_observations = {
        qx_resp: preMessageQxResp || null,
        custom_resps: preMessageCustomResps
          .filter(c => c.jitai_resp)
          .map(c => ({ jitai_resp: c.jitai_resp })),
      }
      group.jitai_setting_list[index].prompt_time_type = promptTimeType
      group.jitai_setting_list[index].prompt_time = promptTime
      group.jitai_setting_list[index].prompt_variable = promptVariable
      group.jitai_setting_list[index].timezone = timezone
      group.jitai_setting_list[index].expiry_duration_minutes = Number(expiryDurationMinutes)
      group.jitai_setting_list[index].present_message_decision_rule = presentMessageDecisionRule
      group.jitai_setting_list[index].message_settings = messageSettings
      setShow(false)
    } else {
      setSaveError(error)
    }
  }

  return (
    <>
      <Card className='p-2 my-2 rounded'>
        <Row style={{ marginLeft: '-5px' }}>
          <Col md={6} style={{ whiteSpace: 'normal' }}>
            {group.jitai_setting_list[index].task_name
              ? group.jitai_setting_list[index].task_name
              : "Click 'Edit' to start setting up"}
          </Col>
          <Col md={3}>
            <Button
              style={{ width: '100%' }}
              variant='primary'
              className='btn-sm m-1'
              onClick={handleOpen}
            >
              Edit
            </Button>
          </Col>
          <Col md={3}>
            <Button
              style={{ width: '100%', marginLeft: '-12px' }}
              variant='danger'
              className='btn-sm my-1'
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Col>
        </Row>
      </Card>

      <ModalContainer
        title='Edit JITAI Settings'
        show={show}
        handleClose={handleClose}
      >
        {saveError && <Message variant='danger'>{saveError}</Message>}

        {/* Task Name */}
        <Row>
          <Col md={6}>
            <Form.Group controlId={'jitai_task_name' + group._id + index}>
              <Form.Label>Task Name *</Form.Label>
              <Form.Control
                type='text'
                placeholder='Enter a unique task name'
                value={taskName}
                onChange={(e) => setTaskName(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        
        {/* Prompt Time Section */}
        <Form.Label>Prompt Time</Form.Label>
        <Row>
          <Col md={6}>
            <Form.Group controlId={'jitai_prompt_time_type' + group._id + index}>
              <Form.Control
                as='select'
                value={promptTimeType}
                onChange={(e) => {
                  setPromptTimeType(e.target.value)
                }}
              >
                <option value=''>—Please Select—</option>
                <option value='Fixed'>Fixed</option>
                <option value='Variable'>Variable</option>
              </Form.Control>
            </Form.Group>
          </Col>
          
          {promptTimeType === 'Fixed' && (
            <Col md={6}>
              <Form.Group controlId={'jitai_prompt_time' + group._id + index}>
                <Form.Control
                  type='time'
                  value={promptTime}
                  onChange={(e) => {
                    setPromptTime(e.target.value)
                  }}
                />
              </Form.Group>
            </Col>
          )}
          
          {promptTimeType === 'Variable' && (
            <Col md={6}>
              <Form.Group controlId={'jitai_prompt_variable' + group._id + index}>
                <Form.Control
                  as='select'
                  value={promptVariable || ''}
                  onChange={(e) => {
                    setPromptVariable(e.target.value)
                  }}
                >
                  <option value=''>—Select JITAI Variable—</option>
                  {jitai_variables && jitai_variables.map((variable) => (
                    <option key={variable._id} value={variable._id}>
                      {variable.code}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
          )}
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group controlId={'jitai_timezone' + group._id + index}>
              <Form.Label>Time Zone</Form.Label>
              <Form.Control
                as='select'
                value={timezone}
                onChange={(e) => {
                  setTimezone(e.target.value)
                }}
              >
                <option value='Hongkong'>Hong Kong</option>
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group controlId={'jitai_expiry_duration' + group._id + index}>
              <Form.Label>Task Expiry Duration (minutes)</Form.Label>
              <div style={{ fontSize: '0.85em', color: '#6c757d', marginBottom: '5px' }}>
                Time window for users to accept the notification (1-1440 minutes)
              </div>
              <Form.Control
                type='number'
                min='1'
                max='1440'
                value={expiryDurationMinutes}
                onChange={(e) => {
                  setExpiryDurationMinutes(e.target.value)
                }}
                placeholder='Default: 30 minutes'
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Pre-Message Observations Section */}
        <div style={{ marginTop: '20px', marginBottom: '10px', padding: '15px', border: '1px solid #dee2e6', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
          <Form.Label style={{ fontWeight: 'bold', fontSize: '1.1em', marginBottom: '10px' }}>
            Pre-Message Observations
          </Form.Label>
          <div style={{ fontSize: '0.85em', color: '#6c757d', marginBottom: '15px' }}>
            Observations associated with the period before the JITAI message is shown.
            Only the pre-message questionnaire feeds the Presentation Decision Rule below;
            pre-message custom responses are computed retrospectively (overnight) for
            export/analytics and are NOT available to the gate (a gate rule that needs such
            a value must compute it directly from its data source at evaluation time).
            Only one questionnaire and any number of custom responses are allowed.
          </div>

          {/* Pre-Message Qx (single) */}
          <div style={{ marginBottom: '15px' }}>
            <Form.Label style={{ fontWeight: 'bold' }}>
              Pre-Message Questionnaire (optional, exactly one)
            </Form.Label>
            <div style={{ fontSize: '0.85em', color: '#6c757d', marginBottom: '8px' }}>
              Shown as a blocking modal when the participant taps 開始 on the JITAI prompt popup.
              If multiple prompts in the popup share this questionnaire, the participant fills it
              once and the response is recorded against each task. If any questionnaire is dismissed
              (稍後) or the app is force-quit, all in-progress answers are discarded and the popup
              re-appears on next app open until the JITAI expiry passes.
            </div>
            <Row>
              <Col md={9}>
                <Form.Group controlId={'jitai_pre_msg_qx' + group._id + index}>
                  <Form.Control
                    as='select'
                    value={preMessageQxResp || ''}
                    onChange={(e) => setPreMessageQxResp(e.target.value || null)}
                  >
                    <option value=''>—No Pre-Message Questionnaire—</option>
                    {jitai_resps && jitai_resps
                      .filter(r => r.resp_type === 'qx')
                      .map(r => (
                        <option key={r._id} value={r._id}>
                          {r.code}
                        </option>
                      ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Button
                  style={{ width: '100%' }}
                  variant='outline-secondary'
                  className='btn-sm'
                  disabled={!preMessageQxResp}
                  onClick={() => setPreMessageQxResp(null)}
                >
                  Clear
                </Button>
              </Col>
            </Row>
          </div>

          {/* Pre-Message Custom Responses (many) */}
          <div>
            <Form.Label style={{ fontWeight: 'bold' }}>
              Pre-Message Custom Responses (optional, many)
            </Form.Label>
            <div style={{ fontSize: '0.85em', color: '#6c757d', marginBottom: '8px' }}>
              Retrieved retrospectively by the nightly performJitaiCustomRespTask Lambda for the
              window before the prompt, then stored as JitaiObservation rows (phase
              "pre_message") for export/analytics. The notification is NOT held back for these,
              and their values are NOT available to the Presentation Decision Rule.
            </div>

            {preMessageCustomResps.map((entry, i) => (
              <Row
                key={'jitai_pre_msg_custom' + group._id + index + i}
                style={{ marginBottom: '8px' }}
              >
                <Col md={9}>
                  <Form.Group controlId={'jitai_pre_msg_custom_resp' + group._id + index + i}>
                    <Form.Control
                      as='select'
                      value={entry.jitai_resp || ''}
                      onChange={(e) => {
                        const updated = [...preMessageCustomResps]
                        updated[i].jitai_resp = e.target.value
                        setPreMessageCustomResps(updated)
                      }}
                    >
                      <option value=''>—Select Custom Response—</option>
                      {jitai_resps && jitai_resps
                        .filter(r => r.resp_type === 'custom')
                        .map(r => (
                          <option key={r._id} value={r._id}>
                            {r.code}
                          </option>
                        ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Button
                    style={{ width: '100%' }}
                    variant='danger'
                    className='btn-sm'
                    onClick={() => handlePreMessageCustomDelete(i)}
                  >
                    Remove
                  </Button>
                </Col>
              </Row>
            ))}

            <Row>
              <Col md={12}>
                <Button
                  style={{ width: '100%', marginTop: '5px' }}
                  variant='outline-primary'
                  onClick={handlePreMessageCustomAdd}
                >
                  + Add Custom Response
                </Button>
              </Col>
            </Row>
          </div>
        </div>

        {/* Collecting Responses Section */}
        <div style={{ marginTop: '20px', marginBottom: '10px', padding: '15px', border: '1px solid #dee2e6', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
          <Form.Label style={{ fontWeight: 'bold', fontSize: '1.1em', marginBottom: '10px' }}>
            Responses to Collect After Task Acceptance
          </Form.Label>
          <div style={{ fontSize: '0.85em', color: '#6c757d', marginBottom: '15px' }}>
            Proximal-outcome responses collected after the participant accepts the JITAI prompt
            (e.g., did activity level change 30 min later). These do NOT feed the Presentation
            Decision Rule — they measure the intervention's downstream effect.
            For questionnaire (qx) type responses, you can optionally set a response window and reminder interval.
          </div>

          {collectingResponses && collectingResponses.length > 0 && (
            <Row style={{ marginBottom: '10px', fontWeight: 'bold', fontSize: '0.9em' }}>
              <Col md={3}>JITAI Response</Col>
              <Col md={2}>Minutes After Task</Col>
              <Col md={2}>Response Window (min)</Col>
              <Col md={2}>Reminder Interval (min)</Col>
              <Col md={3}></Col>
            </Row>
          )}

          {collectingResponses &&
            collectingResponses.map((respSetting, i) => {
              const selectedResp = getJitaiRespById(respSetting.jitai_resp)
              const isQxType = selectedResp && selectedResp.resp_type === 'qx'
              
              return (
                <div 
                  key={'jitai_collecting_resp' + group._id + index + i}
                  style={{ 
                    padding: '10px', 
                    marginBottom: '10px', 
                    border: '1px solid #ced4da', 
                    borderRadius: '6px',
                    backgroundColor: '#ffffff'
                  }}
                >
                  <Row>
                    <Col md={3}>
                      <Form.Group controlId={'jitai_resp_select' + group._id + index + i}>
                        <Form.Control
                          as='select'
                          value={respSetting.jitai_resp || ''}
                          onChange={(e) => {
                            let updated = [...collectingResponses]
                            updated[i].jitai_resp = e.target.value
                            // Reset qx fields when response changes
                            const newResp = getJitaiRespById(e.target.value)
                            if (!newResp || newResp.resp_type !== 'qx') {
                              updated[i].qx_respond_in = ''
                              updated[i].qx_reminder_interval = ''
                            }
                            setCollectingResponses(updated)
                          }}
                        >
                          <option value=''>—Select Response—</option>
                          {jitai_resps && jitai_resps.map((resp) => (
                            <option key={resp._id} value={resp._id}>
                              {resp.code} ({resp.resp_type})
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    
                    <Col md={2}>
                      <Form.Group controlId={'jitai_resp_minutes' + group._id + index + i}>
                        <Form.Control
                          type='number'
                          min='0'
                          placeholder='0'
                          value={respSetting.collected_at_minutes_after_task}
                          onChange={(e) => {
                            let updated = [...collectingResponses]
                            updated[i].collected_at_minutes_after_task = e.target.value
                            setCollectingResponses(updated)
                          }}
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={2}>
                      {isQxType ? (
                        <Form.Group controlId={'jitai_resp_respond_in' + group._id + index + i}>
                          <Form.Control
                            type='number'
                            min='1'
                            placeholder='Optional'
                            value={respSetting.qx_respond_in || ''}
                            onChange={(e) => {
                              let updated = [...collectingResponses]
                              updated[i].qx_respond_in = e.target.value
                              setCollectingResponses(updated)
                            }}
                          />
                        </Form.Group>
                      ) : (
                        <span className='text-muted' style={{ fontSize: '0.85em' }}>N/A</span>
                      )}
                    </Col>
                    
                    <Col md={2}>
                      {isQxType ? (
                        <Form.Group controlId={'jitai_resp_reminder' + group._id + index + i}>
                          <Form.Control
                            type='number'
                            min='1'
                            placeholder='Optional'
                            value={respSetting.qx_reminder_interval || ''}
                            onChange={(e) => {
                              let updated = [...collectingResponses]
                              updated[i].qx_reminder_interval = e.target.value
                              setCollectingResponses(updated)
                            }}
                          />
                        </Form.Group>
                      ) : (
                        <span className='text-muted' style={{ fontSize: '0.85em' }}>N/A</span>
                      )}
                    </Col>
                    
                    <Col md={3}>
                      <Button
                        style={{ width: '100%' }}
                        variant='danger'
                        className='btn-sm'
                        onClick={() => handleCollectingResponseDelete(i)}
                      >
                        Remove
                      </Button>
                    </Col>
                  </Row>
                </div>
              )
            })}

          <Row>
            <Col md={12}>
              <Button
                style={{ width: '100%', marginTop: '5px' }}
                variant='outline-primary'
                onClick={handleCollectingResponseAdd}
              >
                + Add Response to Collect
              </Button>
            </Col>
          </Row>

          {(!jitai_resps || jitai_resps.length === 0) && (
            <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', color: '#856404' }}>
              <i className='fas fa-exclamation-triangle'></i> No JITAI Responses defined for this study. 
              Please create JITAI Responses first from the study list page.
            </div>
          )}
        </div>

        {/* Present Message Decision Rule Section */}
        <Row style={{ marginTop: '15px' }}>
          <Col md={12}>
            <Form.Group controlId={'jitai_present_decision_rule' + group._id + index}>
              <Form.Label>
                Message Presentation Decision Rule (Optional)
              </Form.Label>
              <div style={{ fontSize: '0.85em', color: '#6c757d', marginBottom: '10px' }}>
                If specified, this decision rule will be evaluated first to determine whether the JITAI message should be presented to the user. If the decision rule check fails, the message will not be presented and will not undergo randomization for any of the messages in the section below.
              </div>
              <Form.Control
                as='select'
                value={presentMessageDecisionRule || ''}
                onChange={(e) => {
                  setPresentMessageDecisionRule(e.target.value || null)
                }}
              >
                <option value=''>—No Decision Rule—</option>
                {jitai_decision_rules && jitai_decision_rules
                  .filter((rule) => rule.type === 'presentJitaiMessage')
                  .map((rule) => (
                    <option key={rule._id} value={rule._id}>
                      {rule.code}
                    </option>
                  ))}
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>

        {/* Message Settings Section */}
        <Form.Label style={{ marginTop: '15px' }}>
          Message with the above settings applied
        </Form.Label>
        <div style={{ fontSize: '0.85em', color: '#6c757d', marginBottom: '10px' }}>
          Note: The probabilities must add up to 100%. Messages will be randomly selected based on the specified probability percentages. For personalized message type, the message content will be determined by a decision rule, rather than defined as a fixed JITAI message.
        </div>
        
        {/* Header Row */}
        <Row style={{ marginBottom: '10px', fontWeight: 'bold' }}>
          <Col md={3}>Probability %</Col>
          <Col md={3}>Message Type</Col>
          <Col md={3}>Message/Decision Rule</Col>
          <Col md={3}></Col>
        </Row>
        
        {messageSettings &&
          messageSettings.map((msgSetting, i) => (
            <Row key={'jitai_message_setting' + group._id + index + i}>
              <Col md={3}>
                <Form.Group
                  controlId={'jitai_msg_probability' + group._id + index + i}
                >
                  <Form.Control
                    type='number'
                    min='1'
                    max='100'
                    placeholder='Probability %'
                    value={msgSetting.probability}
                    onChange={(e) => {
                      let tempSettings = [...messageSettings]
                      tempSettings[i].probability = Number(e.target.value)
                      setMessageSettings(tempSettings)
                    }}
                  />
                </Form.Group>
              </Col>
              
              <Col md={3}>
                <Form.Group
                  controlId={'jitai_msg_type' + group._id + index + i}
                >
                  <Form.Control
                    as='select'
                    value={msgSetting.message_type}
                    onChange={(e) => {
                      let tempSettings = [...messageSettings]
                      tempSettings[i].message_type = e.target.value
                      // Reset the selected message/rule when type changes
                      tempSettings[i].jitai_message = null
                      tempSettings[i].jitai_decision_rule = null
                      setMessageSettings(tempSettings)
                    }}
                  >
                    <option value=''>—Select Type—</option>
                    <option value='Generic'>Generic</option>
                    <option value='Personalized'>Personalized</option>
                    <option value='NoMessage'>NoMessage</option>
                  </Form.Control>
                </Form.Group>
              </Col>
              
              <Col md={3}>
                {msgSetting.message_type === 'Generic' && (
                  <Form.Group
                    controlId={'jitai_msg_message' + group._id + index + i}
                  >
                    <Form.Control
                      as='select'
                      value={msgSetting.jitai_message || ''}
                      onChange={(e) => {
                        let tempSettings = [...messageSettings]
                        tempSettings[i].jitai_message = e.target.value
                        setMessageSettings(tempSettings)
                      }}
                    >
                      <option value=''>—Select Message—</option>
                      {jitai_messages && jitai_messages.map((message) => (
                        <option key={message._id} value={message._id}>
                          {message.code}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                )}
                
                {msgSetting.message_type === 'Personalized' && (
                  <Form.Group
                    controlId={'jitai_msg_rule' + group._id + index + i}
                  >
                    <Form.Control
                      as='select'
                      value={msgSetting.jitai_decision_rule || ''}
                      onChange={(e) => {
                        let tempSettings = [...messageSettings]
                        tempSettings[i].jitai_decision_rule = e.target.value
                        setMessageSettings(tempSettings)
                      }}
                    >
                      <option value=''>—Select Decision Rule—</option>
                      {jitai_decision_rules && jitai_decision_rules
                        .filter((rule) => rule.type === 'personalizedJitaiMessage' || !rule.type)
                        .map((rule) => (
                          <option key={rule._id} value={rule._id}>
                            {rule.code}
                          </option>
                        ))}
                    </Form.Control>
                  </Form.Group>
                )}
                
                {msgSetting.message_type === 'NoMessage' && (
                  <span className='text-muted' style={{ fontSize: '0.85em' }}>
                    No message required
                  </span>
                )}
              </Col>
              
              <Col md={3}>
                <Button
                  style={{ width: '100%' }}
                  variant='danger'
                  className='btn-sm'
                  onClick={() => handleMessageSettingDelete(i)}
                >
                  Delete
                </Button>
              </Col>
            </Row>
          ))}

        <Row>
          <Col md={12}>
            <Button
              style={{ width: '100%', marginTop: '5px' }}
              variant='primary'
              onClick={handleMessageSettingAdd}
            >
              Add Message Setting
            </Button>
          </Col>
        </Row>
      </ModalContainer>
    </>
  )
}

export default JitaiSetting
